const responseSelector = "[data-dwm-hcaptcha-response]"
const generatedResponseSelector = "textarea[name='h-captcha-response'], input[name='h-captcha-response']"
const widgetSelector = ".h-captcha"
const logPrefix = "[DWM hCaptcha]"
const installedFlag = "__dwmHCaptchaBridgeInstalled"

function responseFields(scope) {
  return Array.from(scope?.querySelectorAll?.(responseSelector) || [])
}

function generatedResponseFields(form) {
  return Array.from(form?.querySelectorAll?.(generatedResponseSelector) || [])
}

function duplicateResponseFields(form, ownedField) {
  return generatedResponseFields(form).filter((field) => field !== ownedField)
}

function generatedResponseField(form, ownedField) {
  return duplicateResponseFields(form, ownedField).find((field) => field.value)
}

function synchronizeDuplicateResponseFields(form, ownedField, value) {
  const fields = duplicateResponseFields(form, ownedField)

  fields.forEach((field) => {
    field.value = value
  })

  return fields
}

function safeValueLength(field) {
  return field?.value ? field.value.length : 0
}

function safeFormAction(form) {
  const action = form?.getAttribute?.("action") || ""

  try {
    const pathname = new URL(action, "https://dronewashmanager.invalid").pathname

    if (/^\/(?:invitations|organization_user_invitations)\/[^/]+\/signup\/?$/.test(pathname)) {
      return "invitation-signup"
    }

    if (pathname === "/users/login") return "account-login"
    if (pathname.startsWith("/users/")) return "account-form"
  } catch {
    // Fall through to the non-sensitive fallback label.
  }

  return "unknown-form"
}

function safeFormMethod(form) {
  const method = form?.getAttribute?.("method")

  if (!method) return "get"

  const normalizedMethod = String(method).toLowerCase()
  return ["get", "post"].includes(normalizedMethod) ? normalizedMethod : "unknown-method"
}

function safeClearReason(reason) {
  return ["callback: cleared", "turbo:before-cache"].includes(reason) ? reason : "unknown-clear-reason"
}

function formDiagnostics(form, ownedField, generatedField, extraDetails = {}) {
  return {
    action: safeFormAction(form),
    method: safeFormMethod(form),
    ownedFieldPresent: Boolean(ownedField),
    ownedValuePresent: Boolean(ownedField?.value),
    ownedValueLength: safeValueLength(ownedField),
    generatedFieldPresent: Boolean(generatedField),
    generatedValuePresent: Boolean(generatedField?.value),
    generatedValueLength: safeValueLength(generatedField),
    widgetPresent: Boolean(form?.querySelector?.(widgetSelector)),
    ...extraDetails
  }
}

function logInfo(logger, event, details) {
  logger?.info?.(logPrefix, event, details)
}

function logWarn(logger, event, details) {
  logger?.warn?.(logPrefix, event, details)
}

export function applySolvedToken(token, documentObject = globalThis.document, logger = globalThis.console) {
  const fields = responseFields(documentObject)
  const value = token || ""

  fields.forEach((field) => {
    field.value = value
  })

  logInfo(logger, "callback: solved", {
    responseFieldCount: fields.length,
    tokenPresent: Boolean(value),
    tokenLength: value.length
  })
}

export function clearHCaptchaResponses(
  documentObject = globalThis.document,
  logger = globalThis.console,
  reason = "callback: cleared"
) {
  const fields = responseFields(documentObject)

  fields.forEach((field) => {
    field.value = ""
  })

  logInfo(logger, "response: cleared", {
    reason: safeClearReason(reason),
    responseFieldCount: fields.length
  })
}

export function copyGeneratedHCaptchaResponse(form, logger = globalThis.console) {
  const ownedField = form?.querySelector?.(responseSelector)

  if (!ownedField) {
    logWarn(logger, "submit: missing owned response field", formDiagnostics(form, ownedField, null))
    return
  }

  if (ownedField.value) {
    const synchronizedFields = synchronizeDuplicateResponseFields(form, ownedField, ownedField.value)

    if (synchronizedFields.length > 0) {
      logInfo(
        logger,
        "submit: synchronized duplicate responses",
        formDiagnostics(form, ownedField, null, {
          duplicateResponseFieldCount: synchronizedFields.length
        })
      )
      return
    }

    logInfo(logger, "submit: owned response present", formDiagnostics(form, ownedField, null))
    return
  }

  const generatedField = generatedResponseField(form, ownedField)

  if (generatedField) {
    ownedField.value = generatedField.value
    synchronizeDuplicateResponseFields(form, ownedField, generatedField.value)
    logInfo(logger, "submit: copied generated response", formDiagnostics(form, ownedField, generatedField))
    return
  }

  logWarn(logger, "submit: missing response", formDiagnostics(form, ownedField, generatedField))
}

export function installHCaptchaBridge({
  windowObject = globalThis.window,
  documentObject = globalThis.document,
  logger = windowObject?.console || globalThis.console
} = {}) {
  if (!windowObject || !documentObject || windowObject[installedFlag]) return

  windowObject[installedFlag] = true
  windowObject.dwmHCaptchaSolved = (token) => applySolvedToken(token, documentObject, logger)
  windowObject.dwmHCaptchaCleared = () => clearHCaptchaResponses(documentObject, logger)

  documentObject.addEventListener("submit", (event) => {
    if (!(event.target instanceof windowObject.HTMLFormElement)) return

    copyGeneratedHCaptchaResponse(event.target, logger)
  }, true)

  documentObject.addEventListener("turbo:before-cache", () => {
    clearHCaptchaResponses(documentObject, logger, "turbo:before-cache")
  })

  logInfo(logger, "bridge: installed", {
    responseFieldCount: responseFields(documentObject).length
  })
}
